<template>
    <div
        class="w-full rounded-lg flex flex-col justify-center items-center p-8 mt-6"
    >
        <HeaderIcon>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="#5F6D7E" style="opacity:1;"><path  d="M2 20h20v2H2zm2-8h2v7H4zm5 0h2v7H9zm4 0h2v7h-2zm5 0h2v7h-2zM2 7l10-5l10 5v4H2zm10 1a1 1 0 1 0 0-2a1 1 0 0 0 0 2"/></svg>
        </HeaderIcon>

        <div class="w-full max-w-lg flex flex-col justify-center items-center mt-8 z-50">
            <Intro :heading-text="headingText" :sub-text="subText" />

            <div class="w-full z-50 max-w-sm lg:max-w-lg mt-2">
                <div class="mb-4">
                    <label for="name" class="block mb-2 text-sm font-semibold text-primary-font">
                        {{ isStandardPlan ? "Market Name" : "Organization Name" }}
                    </label>
                    <Input
                        id="organization_name"
                        type="text"
                        v-model="formData.payload.organization_name"
                        :placeholder="isStandardPlan ? 'Enter market name' : 'Enter organization name'"
                        class="mt-1 block w-full shadow-none bg-white placeholder:text-text-soft"
                    />
                    <p v-for="error in getValidationErrors('payload.organization_name')" :key="error" class="text-red-500 text-sm">
                        {{ error }}
                    </p>
                </div>
                <div class="mb-4">
                    <label for="country" class="block mb-2 text-sm font-semibold text-primary-font">
                        Country
                    </label>
                    <div class="rounded-lg border border-primary-border px-4 py-1 bg-disabled-bg text-text-disabled">
                        <div class="flex items-center">
                            <img v-if="countries.find((country) => country.code === formData.payload.country)?.flag" :src="countries.find((country) => country.code === formData.payload.country)?.flag" class="w-4 h-4 mr-2 rounded-full" alt="" />
                            {{ countries.find((country) => country.code === formData.payload.country)?.name }}
                        </div>
                    </div>
                </div>
                <div v-if="!isStandardPlan" class="mb-4">
                    <label
                        for="email"
                        class="block mb-2 text-sm font-semibold text-primary-font"
                    >
                        Email
                    </label>
                    <Input
                        id="email"
                        type="email"
                        placeholder="Enter email"
                        v-model="formData.payload.email"
                        required
                        autocomplete="current-email"
                        class="mt-1 block w-full rounded-lg shadow-none bg-white placeholder:text-text-soft"
                    />
                    <p v-for="error in getValidationErrors('payload.email')" :key="error" class="text-red-500 text-sm">
                        {{ error }}
                    </p>
                </div>
                <div class="mb-4">
                    <label
                        for="address"
                        class="block mb-2 text-sm font-semibold text-primary-font"
                    >
                        {{ isStandardPlan ? "Address" : "Main Office Location" }}
                    </label>
                    <div class="mb-2">
                        <Input
                            id="address"
                            type="text"
                            placeholder="Enter address"
                            v-model="formData.payload.address"
                            required
                            class="mt-1 block w-full rounded-lg shadow-none bg-white placeholder:text-text-soft"
                        />
                        <p v-for="error in getValidationErrors('payload.address')" :key="error" class="text-red-500 text-sm">
                            {{ error }}
                        </p>
                    </div>
                    <div class="w-full flex flex-row gap-2">
                        <div class="flex flex-col gap-1">
                            <label
                                v-if="isStandardPlan"
                                for="state"
                                class="block text-sm font-semibold text-primary-font"
                            >
                                {{ isKeLocale ? 'County' : 'State' }}
                            </label>
                            <Combobox id="state" v-model="formData.payload.state_id" :options="apiStates" :label-key="'name'" :value-key="'id'" :placeholder="statePlaceholder" width="w-46 md:w-62" @update:model-value="handleStateChange"></Combobox>
                            <p v-for="error in getValidationErrors('payload.state_id')" :key="error" class="text-red-500 text-sm">
                                {{ error }}
                            </p>
                        </div>
                        <div class="flex flex-col gap-1">
                            <label
                                v-if="isStandardPlan"
                                for="lga"
                                class="block text-sm font-semibold text-primary-font"
                            >
                                {{ isKeLocale ? 'Sub-County' : 'LGA' }}
                            </label>
                            <Combobox id="lga" v-model="formData.payload.lga_id" :options="apiLocalties" :label-key="'name'" :value-key="'id'" :placeholder="localityPlaceholder" width="w-46 md:w-62" :loading="fetchingLocalties"></Combobox>
                            <p v-for="error in getValidationErrors('payload.lga_id')" :key="error" class="text-red-500 text-sm">
                                {{ error }}
                            </p>
                        </div>
                    </div>
                </div>
                <div v-if="!isStandardPlan" class="mb-4">
                    <label
                        class="block text-sm"
                    >
                        <span class="font-semibold text-primary-font">Markets / Hubs</span> 
                        <span class="text-primary-gray ml-2">(Optional)</span>
                    </label>
                    <div
                        class="block mb-2 text-xs"
                    >
                        <span class="text-primary-gray">
                            Add the markets or hubs you manage. You can skip this for now and add them later.
                        </span>
                    </div>
                    <div v-if="formData.payload.hubs.length > 0 && selectedApiCountry" class="w-full flex flex-col justify-center items-center gap-2 pb-3">
                        <HubAccordion
                            v-for="(hub, hubIndex) in formData.payload.hubs"
                            :key="hubIndex"
                            :hub-index="hubIndex"
                            :title="`Market ${hubIndex + 1}`"
                            :hub="hub"
                            :country-id="selectedApiCountry.id"
                            :validation-errors="getValidationErrors()"
                            @update:hub="formData.payload.hubs[hubIndex] = $event"
                            @remove:hub="removeHub(hubIndex)"
                        />
                    </div>
                    <div class="w-full flex flex-col justify-center items-center">
                        <Button
                            variant="ghost"
                            @click="addHub"
                            class="w-full text-primary-gray bg-white border border-primary-border"
                        >
                            <span class="">Add Market</span>
                            <Plus class="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                <div class="w-full flex flex-row justify-center items-center gap-4">
                    <Button
                        variant="ghost"
                        :disabled="isLoading"
                        @click="goBack"
                        class="w-full text-primary-gray bg-white border border-primary-border"
                    >
                        <span class="font-medium">Back</span>
                    </Button>
                    <Button
                        variant="primary"
                        @click="submitOrganizationSetup"
                        :disabled="isLoading"
                        class="w-full py-2 px-4 bg-primary-green text-white font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-primary-green"
                    >
                        <span v-if="isLoading">Processing...</span>
                        <span v-else>Next</span>
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { useLocalization } from "@/composables";
import {useSignupWizardStore, useCountriesStore} from "@/store";
import {useStates, useLocalties} from "@/composables";
import { storeToRefs } from "pinia";
import HeaderIcon from "./components/HeaderIcon.vue";
import Intro from "./components/Intro.vue";
import { useHelpers } from "@/composables/helpers";
import { Combobox } from "@/components/ui/combobox";
import Button from "@/components/ui/button/Button.vue";
import { Plus } from "lucide-vue-next";
import Input from "@/components/ui/input/Input.vue";
import HubAccordion from "./components/HubAccordion.vue";

const {countries} = useHelpers()
const {isKeLocale} = useLocalization()

const signupWizardStore = useSignupWizardStore();
const countriesStore = useCountriesStore();
const states = useStates();
const localties = useLocalties();

const {isLoading, formData, isStandardPlan} = storeToRefs(signupWizardStore)
const {goBack, getValidationErrors, addHub, removeHub, setCountryId, submitOrganizationSetup} = signupWizardStore

const {apiCountries} = storeToRefs(countriesStore)
const {fetchCountries} = countriesStore

const {apiStates} = storeToRefs(states)
const {fetchStates} = states

const {apiLocalties, fetchingLocalties} = storeToRefs(localties)
const {fetchLocalties} = localties

const headingText = "Tell us a bit about your setup";
const subText = "This helps us set things up the right way for you. You can always edit or add more details later.";

const selectedApiCountry = computed(() => {
    return apiCountries.value.find((country) => country.slug === (isKeLocale.value ? "ke" : "ng"));
});

const statePlaceholder = computed(() => {
    return selectedApiCountry.value && selectedApiCountry.value.slug === "ng" ? "Select state" : "Select county";
})

const localityPlaceholder = computed(() => {
    return selectedApiCountry.value && selectedApiCountry.value.slug === "ng" ? "Select LGA" : "Select sub-county";
})

const handleStateChange = async () => {
    await fetchLocalties(formData.value.payload.state_id)
}

onMounted(async () => {
    await fetchCountries()
    setCountryId(selectedApiCountry.value?.id ?? 1)
    await fetchStates(selectedApiCountry.value?.id ?? 1)
    if(formData.value.payload.state_id) {
        await fetchLocalties(formData.value.payload.state_id)
    }
})

</script>
